import { RightPanel } from "../../../components/RightPanel/RightPanel";
import { Grid, Stack, Divider, Typography, Button } from "@mui/material";
import { StatusRenderer } from "../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { PreviewTemplate } from "../../../components/Preview/PreviewTemplate";
import { LoadState } from "../../../constants/enums";
import { formatText } from "../../../helpers";
import WhatsappBgImg from "../../../assets/whatsapp_bg_img.jpg";
import {
  IWhatsAppTemplate,
  IWhatsAppTemplateBody,
  IWhatsAppTemplateButtons,
  IWhatsAppTemplateData,
  IWhatsAppTemplateFooter,
  IWhatsAppTemplateHeader,
} from "../../../redux/whatsappTemplate/whatsappTemplate.types";

interface IViewWhatsappTemplateDetailsProps {
  type: "TEMPLATE_LIST" | "ANALYTICS";
  open: boolean;
  onClose: () => void;
  metaTemplate?: IWhatsAppTemplate | any;
  whatsappTemplateData?: IWhatsAppTemplateData;
}

export const ViewWhatsappTemplateDetails = (
  props: IViewWhatsappTemplateDetailsProps,
) => {
  const { open, metaTemplate, onClose, type, whatsappTemplateData } = props;

  const getComponentByType = (type: string) => {
    if (metaTemplate && metaTemplate?.components) {
      return metaTemplate?.components.find((comp: any) => comp.type === type);
    } else if (metaTemplate?.content) {
      const content = metaTemplate.content;
      if (Array.isArray(content)) {
        for (const template of content) {
          if (template?.components && Array.isArray(template?.components)) {
            const found = template.components.find(
              (comp: any) => comp.type === type,
            );
            if (found) return found;
          } else if (template?.type === type) {
            return template;
          }
        }
      }
    }
    return undefined;
  };

  const getBodyPreviewHtml = (bodyText: string) => {
    if (!bodyText) return "";
    return bodyText.replace(/\{\{(\d+)\}\}/g, (match, p1) => {
      return match;
    });
  };

  const transformToWhatsAppTemplate = (
    data: IWhatsAppTemplate | any,
  ): IWhatsAppTemplateData => {
    if (type === "TEMPLATE_LIST") {
      const header = data.components?.find(
        (comp: any) => comp.type === "HEADER",
      ) as IWhatsAppTemplateHeader;
      const body = data.components?.find(
        (comp: any) => comp.type === "BODY",
      ) as IWhatsAppTemplateBody;
      const footer = data.components?.find(
        (comp: any) => comp.type === "FOOTER",
      ) as IWhatsAppTemplateFooter;
      const buttons = data.components?.find(
        (comp: any) => comp.type === "BUTTONS",
      ) as IWhatsAppTemplateButtons;

      return {
        name: data.name || "",
        category: data.category || "MARKETING",
        example: {
          header_text: header?.text ? [header.text] : [],
          body_text: body?.text ? [body.text] : [],
        },
        components: (data.components || []).map((comp: any) => {
          if (comp.type === "HEADER") {
            const format = comp.format === "TEXT" ? null : comp.format;
            return { ...comp, format };
          }
          return comp;
        }),
        header: header || { type: "HEADER", format: "TEXT", text: "" },
        body: body || { type: "BODY", text: "" },
        buttons: buttons || { type: "BUTTONS", buttons: [] },
        footer: footer || { type: "FOOTER", text: "" },
        status: data.status,
        rejected_reason: data.reason,
        template_id: data.template_id || undefined,
        company_outlet_uuid: data.company_outlet_uuid,
        attachment: [],
        meta_id: data.meta_id,
      };
    } else {
      let allComponents: any[] = [];
      let content = data.content;

      if (typeof content === "string") {
        try {
          content = JSON.parse(content);
        } catch (e) {
          content = null;
        }
      }

      if (content) {
        if (Array.isArray(content)) {
          content.forEach((template: any) => {
            if (template?.components && Array.isArray(template?.components)) {
              allComponents = allComponents.concat(template.components);
            } else if (template?.type) {
              allComponents.push(template);
            } else if (template && typeof template === "object") {
              if (
                template.text !== undefined ||
                template.format !== undefined ||
                template.buttons !== undefined
              ) {
                allComponents.push(template);
              }
            }
          });
        } else if (typeof content === "object") {
          if (content.components && Array.isArray(content.components)) {
            allComponents = content.components;
          } else if (content.type) {
            allComponents.push(content);
          }
        }
      }

      if (
        allComponents.length === 0 &&
        data.components &&
        Array.isArray(data.components)
      ) {
        allComponents = data.components;
      }

      if (allComponents.length === 0 && content && Array.isArray(content)) {
        allComponents = content.filter(
          (item: any) => item && typeof item === "object",
        );
      }

      const header = allComponents.find(
        (comp: any) => comp.type === "HEADER",
      ) as IWhatsAppTemplateHeader;
      let body = allComponents.find(
        (comp: any) => comp.type === "BODY",
      ) as IWhatsAppTemplateBody;

      if (!body && allComponents.length > 0) {
        let textComponent = allComponents.find(
          (comp: any) => comp.text && !comp.type,
        );
        if (!textComponent) {
          textComponent = allComponents.find((comp: any) => comp.text);
        }
        if (!textComponent && allComponents[0]) {
          textComponent = allComponents[0];
        }

        if (textComponent && textComponent.text) {
          body = {
            type: "BODY",
            text: textComponent.text,
          } as IWhatsAppTemplateBody;
        }
      }

      const footer = allComponents.find(
        (comp: any) => comp.type === "FOOTER",
      ) as IWhatsAppTemplateFooter;
      const buttons = allComponents.find(
        (comp: any) => comp.type === "BUTTONS",
      ) as IWhatsAppTemplateButtons;

      return {
        name: data.template_name || "",
        category: data.category || "MARKETING",
        example: {
          header_text: header?.text ? [header.text] : [],
          body_text: body?.text ? [body.text] : [],
        },

        components: allComponents.map((comp: any) => {
          if (comp.type === "HEADER") {
            const format = comp.format === "TEXT" ? null : comp.format;
            return { ...comp, format };
          }
          return comp;
        }),

        header: header || { type: "HEADER", format: "TEXT", text: "" },
        body: body || { type: "BODY", text: "" },
        buttons: buttons || { type: "BUTTONS", buttons: [] },
        footer: footer || { type: "FOOTER", text: "" },
        status: data.status,
        template_id: undefined,
        company_outlet_uuid: "",
        attachment: metaTemplate?.attachments,
        meta_id: data.meta_id,
      };
    }
  };

  const header = getComponentByType("HEADER") as IWhatsAppTemplateHeader;

  const templateData = whatsappTemplateData
    ? whatsappTemplateData
    : transformToWhatsAppTemplate(metaTemplate);

  return (
    <RightPanel
      paperSx={{ maxWidth: "500px" }}
      open={open}
      onClose={onClose}
      heading={
        type === "TEMPLATE_LIST"
          ? "Meta Template Details"
          : metaTemplate
            ? "Template Details"
            : `${formatText(templateData.name)} Preview`
      }
      actionButtons={() => {
        return (
          <Stack direction={"row"} spacing={2} justifyContent={"flex-start"}>
            <Button variant="contained" onClick={onClose}>
              Cancel
            </Button>
          </Stack>
        );
      }}
    >
      <Grid container spacing={2}>
        {/* Basic Info Section */}
        {metaTemplate && (
          <Grid item xs={12}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h5" fontWeight={600}>
                {type === "TEMPLATE_LIST"
                  ? formatText(metaTemplate?.name)
                  : formatText(metaTemplate?.template_name)}
              </Typography>

              <StatusRenderer status={metaTemplate.status} />
            </Stack>
            {type === "TEMPLATE_LIST" && (
              <Typography variant="body2" color="text.secondary" mt={1}>
                Template ID: {metaTemplate?.id}
              </Typography>
            )}
            {type === "ANALYTICS" && (
              <>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Recipient: {metaTemplate?.recipient_number}
                </Typography>

                <Typography variant="body2" color="text.secondary" mt={1}>
                  Sent At: {new Date(metaTemplate.create_ts).toLocaleString()}
                </Typography>
              </>
            )}
          </Grid>
        )}

        {type === "TEMPLATE_LIST" && metaTemplate && (
          <>
            {/* Configuration Section - Only for TEMPLATE_LIST */}
            <Grid item xs={12}>
              <Divider />
              <Typography variant="subtitle1" fontWeight={600} mt={2} mb={1}>
                Template Configuration
              </Typography>

              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Message Category: {metaTemplate.category}
                </Typography>
              </Grid>
            </Grid>
          </>
        )}

        {/* WhatsApp Message Preview */}
        <Grid item xs={12}>
          {metaTemplate && (
            <Typography variant="subtitle1" fontWeight={600} mt={2} mb={1}>
              WhatsApp Message Preview
            </Typography>
          )}

          <Grid
            item
            xs={12}
            md={12}
            borderRadius={2}
            sx={{
              position: "sticky",
              top: 20,
              backgroundColor: "#efeae2",
              backgroundImage: `url(${WhatsappBgImg})`,
              minHeight: metaTemplate ? "55vh" : "84vh",
              backgroundRepeat: "repeat",
              backgroundSize: "auto",
              // backgroundPosition: "center",
              backgroundBlendMode: "multiply",
              mb: 2,
              display: "flex",
              pl: 4,
              pb: 4,
              pr: 10,
              pt: 2,
              alignItems: "flex-end",
            }}
          >
            <PreviewTemplate
              template={templateData}
              loading={LoadState.Loaded}
              headerType={
                templateData
                  ? templateData.header.format || "TEXT"
                  : header?.format || "TEXT"
              }
              bodyVariableValues={{}}
            />
          </Grid>
        </Grid>
      </Grid>
    </RightPanel>
  );
};
